"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.1,
        },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 100,
            damping: 15,
        },
    },
};

const cardHoverVariants = {
    rest: { scale: 1 },
    hover: { 
        scale: 1.02,
        transition: { type: "spring", stiffness: 400, damping: 17 }
    },
};

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
        opacity: 1, 
        y: 0,
        transition: { duration: 0.5, ease: "easeOut" }
    },
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
        opacity: 1, 
        scale: 1,
        transition: { type: "spring", stiffness: 100, damping: 15 }
    },
};
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    Legend,
} from "recharts";
import {
    TrendUp,
    TrendDown,
    CurrencyDollar,
    Storefront,
    ChartLine,
    ChartPie,
    ArrowLeft,
    Receipt,
    Coins,
    Package,
    CaretUp,
    CaretDown,
    Funnel,
    X,
} from "@phosphor-icons/react";
import { Loader2, RefreshCcw, Download, Undo2, DollarSign, Percent, Users, ShieldAlert } from "lucide-react";
import { format, subDays, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { Link } from "react-router-dom";
import axios from "axios";
import { numberWithCommas } from "@/lib/formatCommas";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useSelector } from "react-redux";

const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1/';

// Chart colors
const COLORS = ['#0052CC', '#36B37E', '#FF5630', '#FFAB00', '#6554C0', '#00B8D9', '#FF8B00'];
const GRADIENT_COLORS = {
    revenue: { start: '#0052CC', end: '#36B37E' },
    profit: { start: '#36B37E', end: '#00B8D9' },
    cost: { start: '#FF5630', end: '#FF8B00' },
};

// Summary Card with hover animation
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "blue", index = 0 }) => {
    const colorClasses = {
        blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
        green: "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400",
        red: "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400",
        orange: "bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400",
        purple: "bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400",
    };

    return (
        <motion.div
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            whileHover={{ 
                scale: 1.03, 
                y: -4,
                transition: { type: "spring", stiffness: 400, damping: 17 }
            }}
            whileTap={{ scale: 0.98 }}
            custom={index}
        >
            <Card className="bg-white dark:bg-slate-900 border shadow-sm hover:shadow-lg transition-shadow duration-300 h-full">
                <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{title}</p>
                            <motion.p 
                                className="text-2xl font-bold mt-1"
                                initial={{ opacity: 0, scale: 0.5 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
                            >
                                {value}
                            </motion.p>
                            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
                        </div>
                        <motion.div 
                            className={cn("p-2 rounded-lg", colorClasses[color])}
                            whileHover={{ rotate: [0, -10, 10, -5, 0] }}
                            transition={{ duration: 0.5 }}
                        >
                            <Icon className="h-5 w-5" weight="duotone" />
                        </motion.div>
                    </div>
                    {trend && (
                        <motion.div 
                            className={cn(
                                "flex items-center gap-1 mt-3 text-xs font-medium",
                                trend === "up" ? "text-green-600" : "text-red-600"
                            )}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            {trend === "up" ? <CaretUp className="h-3 w-3" /> : <CaretDown className="h-3 w-3" />}
                            <span>{trendValue}</span>
                            <span className="text-muted-foreground">vs last period</span>
                        </motion.div>
                    )}
                </CardContent>
            </Card>
        </motion.div>
    );
};

export default function SalesReportPage() {
    const { user } = useSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState(true);
    const [reportData, setReportData] = useState(null);
    const [filtersOpen, setFiltersOpen] = useState(true);

    // Check for permission
    const hasPermission = () => {
        if (!user?.role?.permissions) return false;
        const permissions = typeof user.role.permissions === 'string'
            ? JSON.parse(user.role.permissions)
            : user.role.permissions;
        return permissions.includes('ViewReports') || permissions.includes('Settings');
    };

    // Filters
    const [dateRange, setDateRange] = useState("this_month");
    const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
    const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
    const [shopId, setShopId] = useState("");
    const [shops, setShops] = useState([]);

    // Fetch shops for filter
    useEffect(() => {
        const fetchShops = async () => {
            try {
                const res = await axios.get(`${baseUrl}shops`, { withCredentials: true });
                if (res.data.success) setShops(res.data.data || []);
            } catch (e) { console.error("Failed to fetch shops"); }
        };
        fetchShops();
    }, []);

    // Update date range based on preset
    useEffect(() => {
        const now = new Date();
        switch (dateRange) {
            case "today":
                setStartDate(format(now, "yyyy-MM-dd"));
                setEndDate(format(now, "yyyy-MM-dd"));
                break;
            case "last_7_days":
                setStartDate(format(subDays(now, 7), "yyyy-MM-dd"));
                setEndDate(format(now, "yyyy-MM-dd"));
                break;
            case "last_30_days":
                setStartDate(format(subDays(now, 30), "yyyy-MM-dd"));
                setEndDate(format(now, "yyyy-MM-dd"));
                break;
            case "this_month":
                setStartDate(format(startOfMonth(now), "yyyy-MM-dd"));
                setEndDate(format(now, "yyyy-MM-dd"));
                break;
            case "last_month":
                const lastMonth = subMonths(now, 1);
                setStartDate(format(startOfMonth(lastMonth), "yyyy-MM-dd"));
                setEndDate(format(endOfMonth(lastMonth), "yyyy-MM-dd"));
                break;
            case "custom":
                // Keep current dates for custom
                break;
        }
    }, [dateRange]);

    const fetchReportData = async () => {
        setIsLoading(true);
        try {
            const params = { startDate, endDate };
            if (shopId) params.shopId = shopId;

            // Fetch dashboard analytics data which includes most metrics
            const dashboardRes = await axios.get(`${baseUrl}analytics/dashboard`, {
                params,
                withCredentials: true
            });

            // Fetch sales data for trend charts
            const salesRes = await axios.get(`${baseUrl}analytics/sales`, {
                params,
                withCredentials: true
            });

            if (dashboardRes.data.success) {
                const dashboard = dashboardRes.data.data;
                const salesTrend = salesRes.data.success ? salesRes.data.data : [];

                // Build report data structure
                setReportData({
                    // Summary metrics
                    totalRevenue: dashboard.totalRevenue || 0,
                    totalProfit: dashboard.grossProfit || 0,
                    totalCost: (dashboard.totalRevenue || 0) - (dashboard.grossProfit || 0),
                    totalSales: dashboard.totalSales || 0,
                    averageOrderValue: dashboard.totalSales > 0
                        ? (dashboard.totalRevenue / dashboard.totalSales)
                        : 0,

                    // Returns analysis
                    totalReturns: dashboard.returnedSales || 0,
                    returnsAmount: dashboard.totalReturnsAmount || 0,
                    returnRate: dashboard.totalSales > 0
                        ? ((dashboard.returnedSales / dashboard.totalSales) * 100).toFixed(1)
                        : 0,

                    // Payment analysis
                    creditSales: dashboard.unpaidSalesCount || 0,
                    outstandingAmount: dashboard.totalOutstanding || 0,
                    paidSales: (dashboard.totalSales || 0) - (dashboard.unpaidSalesCount || 0),

                    // Type breakdown
                    retailSales: dashboard.retailSales || 0,
                    wholesaleSales: dashboard.wholesaleSales || 0,
                    retailRevenue: dashboard.retailRevenue || 0,
                    wholesaleRevenue: dashboard.wholesaleRevenue || 0,

                    // Profit margin
                    profitMargin: dashboard.totalRevenue > 0
                        ? ((dashboard.grossProfit / dashboard.totalRevenue) * 100).toFixed(1)
                        : 0,

                    // Trend data
                    salesTrend: salesTrend.map(item => ({
                        date: item.date || item.period,
                        revenue: parseFloat(item.revenue || item.total || 0),
                        profit: parseFloat(item.profit || 0),
                        cost: parseFloat(item.cost || 0),
                        orders: item.count || item.orders || 0,
                    })),

                    // Category breakdown (if available)
                    categoryBreakdown: dashboard.categoryBreakdown || [],

                    // Payment method breakdown
                    paymentMethods: dashboard.paymentMethods || [
                        { name: "Cash", value: 60 },
                        { name: "MoMo", value: 25 },
                        { name: "Credit", value: 15 },
                    ],
                });
            }
        } catch (error) {
            console.error("Failed to fetch report data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchReportData();
    }, [startDate, endDate, shopId]);

    const currency = "GHS";

    // Generate chart data for pie charts
    const salesTypeData = reportData ? [
        { name: "Retail", value: reportData.retailRevenue || 0, fill: COLORS[0] },
        { name: "Wholesale", value: reportData.wholesaleRevenue || 0, fill: COLORS[1] },
    ].filter(d => d.value > 0) : [];

    const paymentStatusData = reportData ? [
        { name: "Paid", value: reportData.paidSales || 0, fill: COLORS[1] },
        { name: "Credit", value: reportData.creditSales || 0, fill: COLORS[2] },
    ].filter(d => d.value > 0) : [];

    return (
        <div className="mx-auto p-4 space-y-6">
            {/* Permission Check */}
            {!hasPermission() ? (
                <Card className="mx-auto max-w-md mt-20">
                    <CardContent className="pt-6 text-center">
                        <ShieldAlert className="h-12 w-12 mx-auto text-red-500 mb-4" />
                        <h2 className="text-xl font-bold mb-2">Access Denied</h2>
                        <p className="text-muted-foreground mb-4">You don't have permission to view sales reports.</p>
                        <Link to="/dashboard">
                            <Button>Go to Dashboard</Button>
                        </Link>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Header */}
                    <motion.div 
                        className="flex flex-wrap justify-between items-center gap-4"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                        <div className="flex items-center gap-4">
                            <Link to="/dashboard/reports">
                                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                                    <Button variant="outline" size="icon"><ArrowLeft className="h-4 w-4" /></Button>
                                </motion.div>
                            </Link>
                            <div>
                                <h1 className="text-2xl font-bold">Sales Analytics</h1>
                                <p className="text-sm text-muted-foreground">Cost, profit, and returns analysis</p>
                            </div>
                        </div>
                        <motion.div 
                            className="flex gap-2"
                            whileHover={{ scale: 1.02 }}
                        >
                            <Button variant="outline" onClick={fetchReportData} disabled={isLoading}>
                                <RefreshCcw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                                Refresh
                            </Button>
                        </motion.div>
                    </motion.div>

                    {/* Filters */}
                    <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
                        <Card className="border">
                            <CollapsibleTrigger asChild>
                                <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50">
                                    <div className="flex items-center gap-2">
                                        <Funnel className="h-4 w-4" />
                                        <span className="font-medium">Filters</span>
                                        <Badge variant="outline" className="ml-2">
                                            {format(new Date(startDate), "MMM dd")} - {format(new Date(endDate), "MMM dd, yyyy")}
                                        </Badge>
                                    </div>
                                    {filtersOpen ? <X className="h-4 w-4" /> : <CaretDown className="h-4 w-4" />}
                                </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                                <div className="p-4 pt-0 border-t">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="space-y-2">
                                            <Label>Date Range</Label>
                                            <Select value={dateRange} onValueChange={setDateRange}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="today">Today</SelectItem>
                                                    <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                                                    <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                                                    <SelectItem value="this_month">This Month</SelectItem>
                                                    <SelectItem value="last_month">Last Month</SelectItem>
                                                    <SelectItem value="custom">Custom Range</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {dateRange === "custom" && (
                                            <>
                                                <div className="space-y-2">
                                                    <Label>Start Date</Label>
                                                    <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>End Date</Label>
                                                    <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                                                </div>
                                            </>
                                        )}

                                        <div className="space-y-2">
                                            <Label>Shop</Label>
                                            <Select value={shopId} onValueChange={(val) => setShopId(val === "all" ? "" : val)}>
                                                <SelectTrigger><SelectValue placeholder="All Shops" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">All Shops</SelectItem>
                                                    {shops.map(shop => (
                                                        <SelectItem key={shop.id} value={shop.id.toString()}>{shop.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </div>
                            </CollapsibleContent>
                        </Card>
                    </Collapsible>

                    <AnimatePresence mode="wait">
                    {isLoading ? (
                        <motion.div 
                            className="flex items-center justify-center h-64"
                            key="loader"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                            >
                                <Loader2 className="h-8 w-8 text-primary" />
                            </motion.div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="content"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            {/* Summary Metrics */}
                            <motion.div 
                                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                <MetricCard
                                    title="Total Revenue"
                                    value={`${currency} ${numberWithCommas(reportData?.totalRevenue?.toFixed(2) || "0.00")}`}
                                    icon={CurrencyDollar}
                                    color="blue"
                                    index={0}
                                />
                                <MetricCard
                                    title="Gross Profit"
                                    value={`${currency} ${numberWithCommas(reportData?.totalProfit?.toFixed(2) || "0.00")}`}
                                    subtitle={`${reportData?.profitMargin || 0}% margin`}
                                    icon={Coins}
                                    color="green"
                                    index={1}
                                />
                                <MetricCard
                                    title="Total Cost"
                                    value={`${currency} ${numberWithCommas(reportData?.totalCost?.toFixed(2) || "0.00")}`}
                                    icon={Package}
                                    color="orange"
                                    index={2}
                                />
                                <MetricCard
                                    title="Total Orders"
                                    value={numberWithCommas(reportData?.totalSales || 0)}
                                    subtitle={`Avg: ${currency} ${numberWithCommas(reportData?.averageOrderValue?.toFixed(2) || "0.00")}`}
                                    icon={Receipt}
                                    color="purple"
                                    index={3}
                                />
                                <MetricCard
                                    title="Returns"
                                    value={`${currency} ${numberWithCommas(reportData?.returnsAmount?.toFixed(2) || "0.00")}`}
                                    subtitle={`${reportData?.totalReturns || 0} orders (${reportData?.returnRate || 0}%)`}
                                    icon={Undo2}
                                    color="red"
                                    index={4}
                                />
                                <MetricCard
                                    title="Outstanding"
                                    value={`${currency} ${numberWithCommas(reportData?.outstandingAmount?.toFixed(2) || "0.00")}`}
                                    subtitle={`${reportData?.creditSales || 0} credit sales`}
                                    icon={DollarSign}
                                    color="orange"
                                    index={5}
                                />
                            </motion.div>

                            {/* Charts Row 1: Revenue & Profit Trend */}
                            <motion.div 
                                className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6"
                                initial="hidden"
                                animate="visible"
                                variants={containerVariants}
                            >
                                {/* Area Chart - Revenue & Profit Trend */}
                                <motion.div
                                    variants={fadeInUp}
                                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                                >
                                    <Card className="hover:shadow-lg transition-shadow duration-300">
                                        <CardHeader>
                                            <CardTitle className="text-base flex items-center gap-2">
                                                <motion.span
                                                    initial={{ rotate: 0 }}
                                                    animate={{ rotate: [0, 10, -10, 0] }}
                                                    transition={{ duration: 0.5, delay: 0.5 }}
                                                >
                                                    <ChartLine className="h-4 w-4" />
                                                </motion.span>
                                                Revenue & Profit Trend
                                            </CardTitle>
                                            <CardDescription>Daily revenue vs profit comparison</CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <motion.div 
                                                className="h-[300px]"
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ delay: 0.3, duration: 0.5 }}
                                            >
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <AreaChart data={reportData?.salesTrend || []}>
                                                        <defs>
                                                            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#0052CC" stopOpacity={0.3} />
                                                                <stop offset="95%" stopColor="#0052CC" stopOpacity={0} />
                                                            </linearGradient>
                                                            <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#36B37E" stopOpacity={0.3} />
                                                                <stop offset="95%" stopColor="#36B37E" stopOpacity={0} />
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                                        <YAxis tick={{ fontSize: 11 }} />
                                                        <Tooltip
                                                            formatter={(value) => `${currency} ${numberWithCommas(value.toFixed(2))}`}
                                                            contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                                        />
                                                        <Legend />
                                                        <Area
                                                            type="monotone"
                                                            dataKey="revenue"
                                                            stroke="#0052CC"
                                                            fillOpacity={1}
                                                            fill="url(#colorRevenue)"
                                                            name="Revenue"
                                                        />
                                                        <Area
                                                            type="monotone"
                                                            dataKey="profit"
                                                            stroke="#36B37E"
                                                            fillOpacity={1}
                                                            fill="url(#colorProfit)"
                                                            name="Profit"
                                                        />
                                                    </AreaChart>
                                                </ResponsiveContainer>
                                            </motion.div>
                                        </CardContent>
                                    </Card>
                                </motion.div>

                                {/* Line Chart - Orders Trend */}
                                <motion.div
                                    variants={fadeInUp}
                                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                                >
                                    <Card className="hover:shadow-lg transition-shadow duration-300">
                                        <CardHeader>
                                            <CardTitle className="text-base flex items-center gap-2">
                                                <motion.span
                                                    animate={{ y: [0, -3, 0] }}
                                                    transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
                                                >
                                                    <TrendUp className="h-4 w-4" />
                                                </motion.span>
                                                Orders Trend
                                            </CardTitle>
                                            <CardDescription>Number of orders over time</CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <motion.div 
                                                className="h-[300px]"
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ delay: 0.4, duration: 0.5 }}
                                            >
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={reportData?.salesTrend || []}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                                        <YAxis tick={{ fontSize: 11 }} />
                                                        <Tooltip
                                                            contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                                        />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="orders"
                                                            stroke="#6554C0"
                                                            strokeWidth={2}
                                                            dot={{ fill: "#6554C0", strokeWidth: 2 }}
                                                            name="Orders"
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </motion.div>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            </motion.div>

                            {/* Charts Row 2: Pie Charts */}
                            <motion.div 
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6"
                                initial="hidden"
                                animate="visible"
                                variants={containerVariants}
                            >
                                {/* Sales Type Distribution */}
                                <motion.div
                                    variants={scaleIn}
                                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                                >
                                    <Card className="hover:shadow-lg transition-shadow duration-300 h-full">
                                        <CardHeader>
                                            <CardTitle className="text-base flex items-center gap-2">
                                                <motion.span
                                                    animate={{ rotate: [0, 360] }}
                                                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 5, ease: "easeInOut" }}
                                                >
                                                    <ChartPie className="h-4 w-4" />
                                                </motion.span>
                                                Sales by Type
                                            </CardTitle>
                                            <CardDescription>Retail vs Wholesale distribution</CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <motion.div 
                                                className="h-[250px]"
                                                initial={{ opacity: 0, rotate: -5 }}
                                                animate={{ opacity: 1, rotate: 0 }}
                                                transition={{ delay: 0.4, duration: 0.6 }}
                                            >
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={salesTypeData}
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius={60}
                                                            outerRadius={90}
                                                            paddingAngle={2}
                                                            dataKey="value"
                                                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                        >
                                                            {salesTypeData.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.fill} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip formatter={(value) => `${currency} ${numberWithCommas(value.toFixed(2))}`} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </motion.div>
                                            <motion.div 
                                                className="flex justify-center gap-6 mt-2"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ delay: 0.6 }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[0] }}></div>
                                                    <span className="text-sm">Retail: {currency} {numberWithCommas(reportData?.retailRevenue?.toFixed(2) || "0")}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[1] }}></div>
                                                    <span className="text-sm">Wholesale: {currency} {numberWithCommas(reportData?.wholesaleRevenue?.toFixed(2) || "0")}</span>
                                                </div>
                                            </motion.div>
                                        </CardContent>
                                    </Card>
                                </motion.div>

                                {/* Payment Status Distribution */}
                                <motion.div
                                    variants={scaleIn}
                                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                                >
                                    <Card className="hover:shadow-lg transition-shadow duration-300 h-full">
                                        <CardHeader>
                                            <CardTitle className="text-base flex items-center gap-2">
                                                <motion.span
                                                    animate={{ scale: [1, 1.1, 1] }}
                                                    transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 3 }}
                                                >
                                                    <Storefront className="h-4 w-4" />
                                                </motion.span>
                                                Payment Status
                                            </CardTitle>
                                            <CardDescription>Paid vs Credit sales</CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <motion.div 
                                                className="h-[250px]"
                                                initial={{ opacity: 0, rotate: 5 }}
                                                animate={{ opacity: 1, rotate: 0 }}
                                                transition={{ delay: 0.5, duration: 0.6 }}
                                            >
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={paymentStatusData}
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius={60}
                                                            outerRadius={90}
                                                            paddingAngle={2}
                                                            dataKey="value"
                                                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                        >
                                                            {paymentStatusData.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.fill} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </motion.div>
                                            <motion.div 
                                                className="flex justify-center gap-6 mt-2"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ delay: 0.7 }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[1] }}></div>
                                                    <span className="text-sm">Paid: {reportData?.paidSales || 0} orders</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[2] }}></div>
                                                    <span className="text-sm">Credit: {reportData?.creditSales || 0} orders</span>
                                                </div>
                                            </motion.div>
                                        </CardContent>
                                    </Card>
                                </motion.div>

                                {/* Profit Analysis Card */}
                                <motion.div
                                    variants={scaleIn}
                                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                                >
                                    <Card className="hover:shadow-lg transition-shadow duration-300 h-full">
                                        <CardHeader>
                                            <CardTitle className="text-base flex items-center gap-2">
                                                <motion.span
                                                    animate={{ rotate: [0, 10, -10, 0] }}
                                                    transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 4 }}
                                                >
                                                    <Percent className="h-4 w-4" />
                                                </motion.span>
                                                Profit Analysis
                                            </CardTitle>
                                            <CardDescription>Breakdown of profitability</CardDescription>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            <motion.div 
                                                className="space-y-3"
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.5, duration: 0.5 }}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm text-muted-foreground">Revenue</span>
                                                    <span className="font-medium">{currency} {numberWithCommas(reportData?.totalRevenue?.toFixed(2) || "0")}</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm text-muted-foreground">Cost of Goods</span>
                                                    <span className="font-medium text-orange-600">- {currency} {numberWithCommas(reportData?.totalCost?.toFixed(2) || "0")}</span>
                                                </div>
                                                <div className="border-t pt-2">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm font-medium">Gross Profit</span>
                                                        <span className="font-bold text-green-600">{currency} {numberWithCommas(reportData?.totalProfit?.toFixed(2) || "0")}</span>
                                                    </div>
                                                </div>
                                                <div className="flex justify-between items-center pt-2">
                                                    <span className="text-sm text-muted-foreground">Returns Deduction</span>
                                                    <span className="font-medium text-red-600">- {currency} {numberWithCommas(reportData?.returnsAmount?.toFixed(2) || "0")}</span>
                                                </div>
                                                <div className="border-t pt-2">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm font-medium">Net (After Returns)</span>
                                                        <span className="font-bold">{currency} {numberWithCommas(((reportData?.totalProfit || 0) - (reportData?.returnsAmount || 0)).toFixed(2))}</span>
                                                    </div>
                                                </div>
                                            </motion.div>

                                            <motion.div 
                                                className="pt-4 border-t"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ delay: 0.7 }}
                                            >
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="text-sm">Profit Margin</span>
                                                    <Badge variant={parseFloat(reportData?.profitMargin || 0) >= 20 ? "success" : "secondary"}>
                                                        {reportData?.profitMargin || 0}%
                                                    </Badge>
                                                </div>
                                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                                                    <motion.div
                                                        className="bg-green-500 h-2 rounded-full"
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${Math.min(parseFloat(reportData?.profitMargin || 0), 100)}%` }}
                                                        transition={{ delay: 0.8, duration: 1, ease: "easeOut" }}
                                                    />
                                                </div>
                                            </motion.div>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            </motion.div>

                            {/* Cost Analysis Bar Chart */}
                            <motion.div
                                className="mt-6"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.6, duration: 0.5 }}
                                whileHover={{ y: -3, transition: { duration: 0.2 } }}
                            >
                                <Card className="hover:shadow-lg transition-shadow duration-300">
                                    <CardHeader>
                                        <CardTitle className="text-base">Cost vs Revenue by Period</CardTitle>
                                        <CardDescription>Compare cost of goods against revenue</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <motion.div 
                                            className="h-[300px]"
                                            initial={{ opacity: 0, scale: 0.95 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: 0.8, duration: 0.5 }}
                                        >
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={reportData?.salesTrend || []}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                                    <YAxis tick={{ fontSize: 11 }} />
                                                    <Tooltip
                                                        formatter={(value) => `${currency} ${numberWithCommas(value.toFixed(2))}`}
                                                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                                    />
                                                    <Legend />
                                                    <Bar dataKey="revenue" fill="#0052CC" name="Revenue" radius={[4, 4, 0, 0]} />
                                                    <Bar dataKey="cost" fill="#FF5630" name="Cost" radius={[4, 4, 0, 0]} />
                                                    <Bar dataKey="profit" fill="#36B37E" name="Profit" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </motion.div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </motion.div>
                    )}
                    </AnimatePresence>
                </>
            )}
        </div>
    );
}
